import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { setUserAction, removeUserAction } from './reducer'
import type { AuthUser, AuthStoredData } from 'Types'
import type { AppDispatch } from 'slices'
import { clearBranch, setCanSeeGlobalView } from 'slices/branchSlice'
import axios from 'api/axiosConfig'
import * as SyncService from 'services/SyncService'
import { publishPosDeviceSyncHeartbeat } from 'services/posDeviceSyncHeartbeat'
import { hydrateOfflineCaches } from 'services/offlineCacheHydration'
import { runOfflineBootstrap } from 'services/offlineBootstrap'
import { checkConnectivity } from 'utils/connectivityCheck'
import { preloadPosIfPermitted } from 'utils/preloadPosRoute'

function readUserFromStorage(): AuthUser | null {
 const raw = localStorage.getItem('authUser')
 if (!raw) return null
 try {
 const parsed = JSON.parse(raw) as AuthStoredData & Partial<AuthUser>
 if (parsed.user && parsed.token) {
 return { ...parsed.user, token: parsed.token } as AuthUser
 }
 return parsed as unknown as AuthUser
 } catch {
 return null
 }
}

export const AuthSync: React.FC = () => {
 const dispatch = useDispatch<AppDispatch>()

 useEffect(() => {
 SyncService.startAutoSync()
 void checkConnectivity().then((online) => {
 if (online) void publishPosDeviceSyncHeartbeat()
 })
 return () => {
 SyncService.stopAutoSync()
 }
 }, [])

 useEffect(() => {
 const maybeHydrate = () => {
 if (!readUserFromStorage()?.token) return
 void checkConnectivity().then((isOnline) => {
 if (!isOnline) void hydrateOfflineCaches(dispatch)
 }).catch(() => {})
 }
 maybeHydrate()
 window.addEventListener('offline', maybeHydrate)
 window.addEventListener('auth:updated', maybeHydrate)
 return () => {
 window.removeEventListener('offline', maybeHydrate)
 window.removeEventListener('auth:updated', maybeHydrate)
 }
 }, [dispatch])

 useEffect(() => {
 const syncFromStorage = () => {
 const u = readUserFromStorage()
 if (u) {
 dispatch(setUserAction(u))
 dispatch(setCanSeeGlobalView(Boolean(u.can_use_global_view)))
 void runOfflineBootstrap({ reason: 'auth-restore' }).catch(() => {})
 } else {
 dispatch(removeUserAction())
 dispatch(clearBranch())
 }
 }

 const onStorage = (e: StorageEvent) => {
 if (e.key === 'authUser') syncFromStorage()
 }

 const onAuthUpdated = () => syncFromStorage()

 window.addEventListener('storage', onStorage)
 window.addEventListener('auth:updated', onAuthUpdated)

 syncFromStorage()

 preloadPosIfPermitted()

 return () => {
 window.removeEventListener('storage', onStorage)
 window.removeEventListener('auth:updated', onAuthUpdated)
 }
 }, [dispatch])

 useEffect(() => {
 const triggerBootstrap = (reason: string) => {
 if (!readUserFromStorage()?.token) return
 void runOfflineBootstrap({ reason }).catch(() => {})
 }

 const onBranchChanged = () => triggerBootstrap('branch-changed')
 const onOnline = () => triggerBootstrap('online')

 window.addEventListener('branch:changed', onBranchChanged)
 window.addEventListener('online', onOnline)
 return () => {
 window.removeEventListener('branch:changed', onBranchChanged)
 window.removeEventListener('online', onOnline)
 }
 }, [])

 useEffect(() => {
 let cancelled = false
 const u = readUserFromStorage()
 if (!u?.token) return
 if (Array.isArray(u.permissions) && u.permissions.length > 0) return

 axios
 .get<{ status?: string; data?: { user?: AuthUser } }>('/auth/me')
 .then((res) => {
 if (cancelled) return
 const nextUser = res.data?.data?.user
 if (!nextUser) return
 const rawPrev = localStorage.getItem('authUser')
 let tenantSlug: string | undefined
 if (rawPrev) {
 try {
 const prev = JSON.parse(rawPrev) as { tenant_slug?: string }
 tenantSlug = prev.tenant_slug
 } catch {
 /* ignore */
 }
 }
 if (!tenantSlug) {
 tenantSlug = localStorage.getItem('tenant_slug') || undefined
 }
 localStorage.setItem(
 'authUser',
 JSON.stringify({
 user: nextUser,
 token: u.token,
 ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
 })
 )
 dispatch(setUserAction({ ...nextUser, token: u.token } as AuthUser))
 dispatch(setCanSeeGlobalView(Boolean(nextUser.can_use_global_view)))
 })
 .catch(() => {})

 return () => {
 cancelled = true
 }
 }, [dispatch])

 return null
}
